import * as React from "react";

export type ToastVariant = "default" | "success" | "error" | "warn" | "info";

export interface ToastItem {
  id: number;
  message: string;
  variant: ToastVariant;
}

interface ToastApi {
  show: (msg: string) => void;
  success: (msg: string) => void;
  error: (msg: string) => void;
  warn: (msg: string) => void;
  info: (msg: string) => void;
}

interface ToastContextValue extends ToastApi {
  toasts: ToastItem[];
  dismiss: (id: number) => void;
}

const Context = React.createContext<ToastContextValue | null>(null);

const DURATION = 2400;
const MAX = 3;

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = React.useState<ToastItem[]>([]);

  const dismiss = React.useCallback((id: number) => {
    setToasts((cur) => cur.filter((t) => t.id !== id));
  }, []);

  const push = React.useCallback(
    (message: string, variant: ToastVariant) => {
      const id = Date.now() + Math.random();
      setToasts((cur) => {
        const next = [...cur, { id, message, variant }];
        return next.length > MAX ? next.slice(next.length - MAX) : next;
      });
      setTimeout(() => dismiss(id), DURATION);
    },
    [dismiss],
  );

  const value = React.useMemo<ToastContextValue>(
    () => ({
      toasts,
      dismiss,
      show: (m) => push(m, "default"),
      success: (m) => push(m, "success"),
      error: (m) => push(m, "error"),
      warn: (m) => push(m, "warn"),
      info: (m) => push(m, "info"),
    }),
    [toasts, dismiss, push],
  );

  return <Context.Provider value={value}>{children}</Context.Provider>;
}

export function useToast(): ToastApi {
  const ctx = React.useContext(Context);
  if (!ctx) throw new Error("useToast must be used within <ToastProvider>");
  return ctx;
}

export function useToastList(): {
  toasts: ToastItem[];
  dismiss: (id: number) => void;
} {
  const ctx = React.useContext(Context);
  if (!ctx) throw new Error("useToastList must be used within <ToastProvider>");
  return { toasts: ctx.toasts, dismiss: ctx.dismiss };
}
